// src/theme/components/alert.js
import { alertAnatomy } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
import { mode, transparentize } from "@chakra-ui/theme-tools";

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(alertAnatomy.keys);

const STATUS_TOKENS = {
  success: {
    bg: "successBg",
    icon: "successIcon",
    text: "successText",
    border: "successBorder",
  },
  error: {
    bg: "errorBg",
    icon: "errorIcon",
    text: "errorText",
    border: "errorBorder",
  },
  warning: {
    bg: "warningBg",
    icon: "warningIcon",
    text: "warningText",
    border: "warningBorder",
  },
  info: {
    bg: "infoBg",
    icon: "infoIcon",
    text: "infoText",
    border: "infoBorder",
  },
};

// AI Temali "subtle" Alert Varyanti (Chakra'nin temelini theme tokenlarla hizalar)
const subtleVariant = definePartsStyle((props) => {
  const { colorScheme: c, status, theme } = props; // status: "error", "success", "warning", "info"
  const tokens = STATUS_TOKENS[status];

  const fallbackBg = mode(`${c}.50`, transparentize(`${c}.400`, 0.2)(theme))(props);
  const fallbackBorder = mode(`${c}.200`, transparentize(`${c}.400`, 0.4)(theme))(props);
  const fallbackIcon = mode(`${c}.500`, `${c}.300`)(props);

  const bg = tokens?.bg ?? fallbackBg;
  const borderColor = tokens?.border ?? fallbackBorder;
  const iconColor = tokens?.icon ?? fallbackIcon;
  const titleColor = tokens?.text ?? "textPrimary";

  return {
    container: {
      bg: bg,
      borderRadius: "lg",
      padding: 4,
      borderWidth: "1px",
      borderColor: borderColor,
    },
    icon: {
      color: iconColor,
      boxSize: 5,
      marginEnd: 3,
    },
    title: {
      fontWeight: "semibold",
      fontSize: "md",
      color: titleColor,
    },
    description: {
      fontSize: "sm",
      color: "textSecondary",
    },
    spinner: {
      color: iconColor,
    },
  };
});

// AI Temali "solid" Alert Varyanti
const solidVariant = definePartsStyle((props) => {
  const { colorScheme: c } = props;
  let bg = `${c}.500`;
  let color = "white";

  if (props.colorMode === "dark") {
    bg = `${c}.300`;
    color = "gray.900";
    if (c === "yellow") color = "gray.900";
  }

  return {
    container: {
      bg: c === "brand" ? "accent" : bg,
      color: color,
      borderRadius: "lg",
      padding: 4,
      boxShadow: "md",
    },
    icon: {
      color: color,
      opacity: 0.9,
    },
    title: {
      fontWeight: "bold",
      color: "inherit",
    },
    description: {
      color: "inherit",
      opacity: 0.9,
    },
  };
});

export const alertTheme = defineMultiStyleConfig({
  baseStyle: definePartsStyle({
    container: {},
    title: {},
    description: {},
  }),
  variants: {
    subtle: subtleVariant,
    solid: solidVariant,
  },
  defaultProps: {
    variant: "subtle",
    colorScheme: "blue",
  },
});

export default alertTheme; // index.js'de Alert: alertTheme olarak kullanilacak
